import React, { useState } from 'react'
import { useRevFinOpsMetrics } from '@/hooks/useRevFinOpsMetrics'
import { useRecentActivities } from '@/hooks/useRecentActivities'
import { RevFinOpsMetricCard } from '@/components/revfinops/RevFinOpsMetricCard'
import { NavigationTabs } from '@/components/revfinops/NavigationTabs'
import { ActivityFeedCard } from '@/components/revfinops/ActivityFeedCard'
import { ProtectedRevFinOpsRoute } from '@/components/revfinops/ProtectedRevFinOpsRoute'
import { useNavigate } from '@tanstack/react-router'
import {
  BarChart3,
  TrendingUp,
  Building2,
  DollarSign,
  Briefcase,
  RefreshCw,
  PlusCircle,
  CheckCircle,
  List
} from 'lucide-react'

export default function RevFinOpsPage() {
  const { metrics, isLoading: metricsLoading, error: metricsError, refresh } = useRevFinOpsMetrics()
  const { activities, loading: activitiesLoading, error: activitiesError, refresh: refreshActivities } = useRecentActivities()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [activeTab, setActiveTab] = useState('dashboard')
  const navigate = useNavigate()

  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true)
    await Promise.all([refresh(), refreshActivities()])
    setIsRefreshing(false)
  }

  // Keyboard accessibility handler
  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }

  // Quick action handlers
  const handleNewClient = () => {
    navigate({ to: '/revfinops/clients/new' })
  }

  const handleApprovePayables = () => {
    navigate({ to: '/revfinops/payables' })
  }

  const handleViewAssignments = () => {
    // TODO: Create /revfinops/assignments route
    alert('Assignments functionality coming soon')
  }

  const handleTriggerIngestion = () => {
    // For now, just show an alert - can be updated to modal later
    alert('Ingestion trigger functionality coming soon')
  }

  // Tab navigation handler
  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey)
    if (tabKey === 'clients') {
      navigate({ to: '/revfinops/clients' })
    } else if (tabKey === 'payables') {
      navigate({ to: '/revfinops/payables' })
    } else if (tabKey === 'assignments') {
      // TODO: Create /revfinops/assignments route
      alert('Assignments functionality coming soon')
    }
  }

  return (
    <ProtectedRevFinOpsRoute>
      
        <div className="space-y-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              RevFinOps
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Gestão de receitas, operações financeiras e clientes
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="btn-ghost flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Atualizar</span>
            </button>
            <div className="text-sm text-brand-gray font-body bg-brand-dark-light/50 px-4 py-2 rounded-monumenta border border-gray-800/50">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
                <span>Sistema em desenvolvimento</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="dashboard-grid">
          <div className="animate-fade-in-up">
            <RevFinOpsMetricCard
              title="Total de Clientes"
              value={metrics.totalClients}
              icon={Building2}
              loading={metricsLoading}
              error={metricsError || undefined}
              onRetry={refresh}
            />
          </div>

          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <RevFinOpsMetricCard
              title="Pagamentos Pendentes"
              value={metrics.pendingPayables}
              icon={DollarSign}
              loading={metricsLoading}
              error={metricsError || undefined}
              onRetry={refresh}
            />
          </div>

          <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <RevFinOpsMetricCard
              title="Contratos Ativos"
              value={metrics.activeAssignments}
              icon={Briefcase}
              loading={metricsLoading}
              error={metricsError || undefined}
              onRetry={refresh}
            />
          </div>

          <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <RevFinOpsMetricCard
              title="Receita do Mês"
              value={metrics.totalRevenue > 0 ? `R$ ${metrics.totalRevenue.toLocaleString('pt-BR')}` : '-'}
              icon={TrendingUp}
              loading={metricsLoading}
              error={metricsError || undefined}
              onRetry={refresh}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <NavigationTabs
          tabs={[
            { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { key: 'clients', label: 'Clientes', icon: Building2 },
            { key: 'payables', label: 'Pagamentos', icon: DollarSign },
            { key: 'assignments', label: 'Contratos', icon: Briefcase }
          ]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="animate-fade-in-up"
        />

        {/* Quick Actions Card */}
        <div className="card animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <h2 className="text-xl font-display font-semibold text-white mb-4">
            Ações Rápidas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={handleNewClient}
              onKeyDown={(e) => handleKeyDown(e, handleNewClient)}
              tabIndex={0}
              className="btn-primary"
            >
              <PlusCircle size={20} />
              <span>Novo Cliente</span>
            </button>
            <button
              onClick={handleApprovePayables}
              onKeyDown={(e) => handleKeyDown(e, handleApprovePayables)}
              tabIndex={0}
              className="btn-secondary"
            >
              <CheckCircle size={20} />
              <span>Aprovar Pagamentos</span>
            </button>
            <button
              onClick={handleViewAssignments}
              onKeyDown={(e) => handleKeyDown(e, handleViewAssignments)}
              tabIndex={0}
              className="btn-ghost"
            >
              <List size={20} />
              <span>Ver Contratos</span>
            </button>
            <button
              onClick={handleTriggerIngestion}
              onKeyDown={(e) => handleKeyDown(e, handleTriggerIngestion)}
              tabIndex={0}
              className="btn-ghost"
            >
              <RefreshCw size={20} />
              <span>Processar Transações</span>
            </button>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <ActivityFeedCard
            activities={activities}
            loading={activitiesLoading}
            error={activitiesError}
          />
        </div>

        {/* Coming Soon Section */}
        <div className="card animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="h-10 w-10 text-brand-primary" />
            </div>
            <h2 className="text-2xl font-bold text-white font-display mb-3">
              Funcionalidades em Desenvolvimento
            </h2>
            <p className="text-brand-gray-light font-body text-lg max-w-2xl mx-auto mb-6">
              O módulo RevFinOps está sendo construído para oferecer gestão completa de clientes,
              pagamentos, contratos e análises financeiras.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <span className="px-4 py-2 bg-brand-dark-light rounded-monumenta text-brand-gray-light font-body text-sm border border-gray-800/50">
                Gestão de Clientes
              </span>
              <span className="px-4 py-2 bg-brand-dark-light rounded-monumenta text-brand-gray-light font-body text-sm border border-gray-800/50">
                Aprovação de Pagamentos
              </span>
              <span className="px-4 py-2 bg-brand-dark-light rounded-monumenta text-brand-gray-light font-body text-sm border border-gray-800/50">
                Contratos e SOWs
              </span>
              <span className="px-4 py-2 bg-brand-dark-light rounded-monumenta text-brand-gray-light font-body text-sm border border-gray-800/50">
                Análise de Receitas
              </span>
            </div>
          </div>
        </div>
      </div>
    
    </ProtectedRevFinOpsRoute>
  )
}
